import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useMediaQuery } from '@mui/material';
import { useCSSVariable } from '../hooks/use-css-variable';
import {
    applyColorSchemeBodyClass,
    ColorSchemePreference,
    loadColorSchemePreference,
    resolveColorSchemeIsDark,
    saveColorSchemePreference
} from '../util/color-scheme-preference';

type ColorSchemeContextValue = {
    preference: ColorSchemePreference;
    setPreference: (preference: ColorSchemePreference) => void;
    isDark: boolean;
};

/**
 * The seam `ColorSchemeUserSettingSync` uses to join this provider to the signed-in user's
 * server-side setting (UX-930). It is a second context because the provider sits ABOVE
 * `SessionProvider` in every shell (the theme has to exist before there is a session, and on the
 * login page there never is one), so the provider itself cannot know the user. Nothing else
 * should use it.
 */
export type ColorSchemeUserSettingLink = {
    /** The user's stored choice has arrived: show it and rewrite the device-level key to match. */
    adoptUserPreference: (preference: ColorSchemePreference) => void;
    /** Where `setPreference` also sends a change while a user is signed in; `null` when none is. */
    setUserPreferenceWriter: (writer: ((preference: ColorSchemePreference) => void) | null) => void;
};

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);
const ColorSchemeUserSettingLinkContext = createContext<ColorSchemeUserSettingLink | null>(null);

export function ColorSchemeProvider({ children }: PropsWithChildren) {
    // The device-level key: all there is before a user is known, and all there ever is signed out.
    const [preference, setPreferenceState] =
        useState<ColorSchemePreference>(loadColorSchemePreference);
    const userPreferenceWriterRef = useRef<((preference: ColorSchemePreference) => void) | null>(
        null
    );
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
    const darkEnabled = useCSSVariable(
        document.getElementsByTagName('head')[0],
        document.getElementsByTagName('body')[0],
        '--dark-mode-enabled'
    );

    const isDark = useMemo(
        () => resolveColorSchemeIsDark(preference, prefersDarkMode, darkEnabled === '1'),
        [preference, prefersDarkMode, darkEnabled]
    );

    const setPreference = useCallback((nextPreference: ColorSchemePreference) => {
        setPreferenceState(nextPreference);
        saveColorSchemePreference(nextPreference);
        userPreferenceWriterRef.current?.(nextPreference);
    }, []);

    useEffect(() => {
        applyColorSchemeBodyClass(isDark);
    }, [isDark]);

    const value = useMemo(
        () => ({
            preference,
            setPreference,
            isDark
        }),
        [preference, setPreference, isDark]
    );

    const link = useMemo<ColorSchemeUserSettingLink>(
        () => ({
            adoptUserPreference: userPreference => {
                setPreferenceState(userPreference);
                if (loadColorSchemePreference() !== userPreference) {
                    saveColorSchemePreference(userPreference);
                }
            },
            setUserPreferenceWriter: writer => {
                userPreferenceWriterRef.current = writer;
            }
        }),
        []
    );

    return (
        <ColorSchemeContext.Provider value={value}>
            <ColorSchemeUserSettingLinkContext.Provider value={link}>
                {children}
            </ColorSchemeUserSettingLinkContext.Provider>
        </ColorSchemeContext.Provider>
    );
}

export function useColorSchemeOptional() {
    return useContext(ColorSchemeContext);
}

export function useColorScheme() {
    const context = useColorSchemeOptional();
    if (!context) {
        throw new Error('useColorScheme must be used within a ColorSchemeProvider');
    }
    return context;
}

/** For `ColorSchemeUserSettingSync` only. `null` outside a `ColorSchemeProvider`. */
export function useColorSchemeUserSettingLink() {
    return useContext(ColorSchemeUserSettingLinkContext);
}
